import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Panel from "../conponents/common/Panel";
import {
  fetchAccount,
  updateAccount,
  clearAccountError,
  selectAccount,
  selectAccountLoading,
  selectAccountUpdating,
  selectAccountError,
  selectAccountUpdateError,
} from "../store/accountSlice";
import { toast } from "react-toastify";
function Users() {
  const dispatch = useDispatch();
  const account = useSelector(selectAccount);
  const isLoading = useSelector(selectAccountLoading);
  const isUpdating = useSelector(selectAccountUpdating);
  const error = useSelector(selectAccountError);
  const updateError = useSelector(selectAccountUpdateError);

  // 表單狀態
  const [formData, setFormData] = useState({
    account: "",
    password: "",
    name: "",
    mobile: "",
    address: "",
    company_id: "",
    contract_capacity: "",
  });

  // 編輯模式狀態
  const [isEditMode, setIsEditMode] = useState(false);

  // 初始化時獲取帳戶資料
  useEffect(() => {
    dispatch(fetchAccount());
  }, [dispatch]);

  // 當帳戶資料載入時更新表單
  useEffect(() => {
    if (account) {
      setFormData({
        account: account.account || "",
        password: account.password || "",
        name: account.name || "",
        mobile: account.mobile || "",
        address: account.address || "",
        company_id: account.company_id || "",
        contract_capacity: account.contract_capacity || "",
      });
    }
  }, [account]);

  // 處理輸入變更
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // 限制公司統編和合約容量只能輸入數字
    if (name === "company_id" || name === "contract_capacity") {
      // 只允許數字，移除所有非數字字符
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // 處理表單提交
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const res = await dispatch(updateAccount(formData)).unwrap();
      if (res.error === 0) {
        setIsEditMode(false);
        dispatch(fetchAccount());
        // 顯示提示訊息
        toast.success(res.msg);
      } else {
        // 顯示提示訊息
        toast.error(res.msg);
      }
    } catch (error) {
      console.error("更新失敗:", error);
    }
  };

  // 取消編輯
  const handleCancel = () => {
    // 重置表單為原始數據
    if (account) {
      setFormData({
        account: account.account || "",
        password: account.password || "",
        name: account.name || "",
        mobile: account.mobile || "",
        address: account.address || "",
        company_id: account.company_id || "",
        contract_capacity: account.contract_capacity || "",
      });
    }
    setIsEditMode(false);
  };

  // 清除錯誤
  const handleClearError = () => {
    dispatch(clearAccountError());
  };

  if (isLoading) {
    return (
      <Panel>
        <div className="w-full">
          <div className="flex items-center justify-center py-8">
            <div className="text-lg text-gray-300">載入中...</div>
          </div>
        </div>
      </Panel>
    );
  }

  return (
    <Panel>
      <div className="w-full">
        {/* 頭部 */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold mb-2">用戶管理</h1>
            <p className="text-sm sm:text-base text-gray-300">
              管理用戶帳戶資訊和設定
            </p>
          </div>

          {/* 操作按鈕 */}
          <div className="flex items-center gap-2">
            {!isEditMode ? (
              <button
                onClick={() => setIsEditMode(true)}
                className="px-4 py-2 rounded-lg border-2 bg-blue-600 text-white border-blue-500 hover:bg-blue-700 transition-all"
              >
                編輯資料
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleSubmit}
                  disabled={isUpdating}
                  className="px-4 py-2 rounded-lg border-2 bg-green-600 text-white border-green-500 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isUpdating ? "儲存中..." : "儲存"}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isUpdating}
                  className="px-4 py-2 rounded-lg border-2 bg-transparent text-gray-300 border-gray-500 hover:bg-gray-600/20 disabled:opacity-50 transition-all"
                >
                  取消
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 錯誤訊息 */}
        {(error || updateError) && (
          <div className="mb-6 p-4 bg-red-600/20 border border-red-500 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="text-red-300">{error || updateError}</div>
              <button
                onClick={handleClearError}
                className="text-red-300 hover:text-red-100 transition-colors"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* 表單內容 */}
        <div className="bg-black/20 rounded-lg p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold mb-6 flex items-center gap-2">
            帳戶資訊
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 帳號 */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  帳號
                </label>
                <input
                  type="text"
                  name="account"
                  value={formData.account}
                  onChange={handleInputChange}
                  disabled={!isEditMode}
                  className={`w-full px-3 py-2 rounded-lg border-2 bg-black/30 text-white transition-all ${
                    isEditMode
                      ? "border-gray-500 focus:border-blue-500 focus:outline-none"
                      : "border-gray-600 cursor-not-allowed opacity-70"
                  }`}
                  placeholder="請輸入帳號"
                />
              </div>

              {/* 密碼 */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  設定新密碼
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={!isEditMode}
                  className={`w-full px-3 py-2 rounded-lg border-2 bg-black/30 text-white transition-all ${
                    isEditMode
                      ? "border-gray-500 focus:border-blue-500 focus:outline-none"
                      : "border-gray-600 cursor-not-allowed opacity-70"
                  }`}
                  placeholder="請輸入新密碼"
                />
              </div>

              {/* 姓名 */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  姓名
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={!isEditMode}
                  className={`w-full px-3 py-2 rounded-lg border-2 bg-black/30 text-white transition-all ${
                    isEditMode
                      ? "border-gray-500 focus:border-blue-500 focus:outline-none"
                      : "border-gray-600 cursor-not-allowed opacity-70"
                  }`}
                  placeholder="請輸入姓名"
                />
              </div>

              {/* 手機號碼 */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  手機號碼
                </label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  disabled={!isEditMode}
                  className={`w-full px-3 py-2 rounded-lg border-2 bg-black/30 text-white transition-all ${
                    isEditMode
                      ? "border-gray-500 focus:border-blue-500 focus:outline-none"
                      : "border-gray-600 cursor-not-allowed opacity-70"
                  }`}
                  placeholder="請輸入手機號碼"
                />
              </div>

              {/* 公司統編 */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  公司統編
                </label>
                <input
                  type="text"
                  name="company_id"
                  value={formData.company_id}
                  onChange={handleInputChange}
                  disabled={!isEditMode}
                  maxLength="8"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  className={`w-full px-3 py-2 rounded-lg border-2 bg-black/30 text-white transition-all ${
                    isEditMode
                      ? "border-gray-500 focus:border-blue-500 focus:outline-none"
                      : "border-gray-600 cursor-not-allowed opacity-70"
                  }`}
                  placeholder="請輸入8位數公司統編"
                />
              </div>

              {/* 合約容量 */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  合約容量 (kW)
                </label>
                <input
                  type="text"
                  name="contract_capacity"
                  value={formData.contract_capacity}
                  onChange={handleInputChange}
                  disabled={!isEditMode}
                  pattern="[0-9]*"
                  inputMode="numeric"
                  className={`w-full px-3 py-2 rounded-lg border-2 bg-black/30 text-white transition-all ${
                    isEditMode
                      ? "border-gray-500 focus:border-blue-500 focus:outline-none"
                      : "border-gray-600 cursor-not-allowed opacity-70"
                  }`}
                  placeholder="請輸入合約容量 (僅限數字)"
                />
              </div>
            </div>

            {/* 地址 - 全寬度 */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                地址
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                disabled={!isEditMode}
                rows={3}
                className={`w-full px-3 py-2 rounded-lg border-2 bg-black/30 text-white transition-all resize-none ${
                  isEditMode
                    ? "border-gray-500 focus:border-blue-500 focus:outline-none"
                    : "border-gray-600 cursor-not-allowed opacity-70"
                }`}
                placeholder="請輸入地址"
              />
            </div>
          </form>
        </div>

        {/* 資料狀態顯示 */}
        {account && (
          <div className="mt-6 bg-black/20 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4">帳戶狀態</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-green-600/20 border border-green-500 rounded-lg p-3">
                <div className="text-green-300 text-sm">狀態</div>
                <div className="text-white font-semibold">已驗證</div>
              </div>
              <div className="bg-blue-600/20 border border-blue-500 rounded-lg p-3">
                <div className="text-blue-300 text-sm">帳戶類型</div>
                <div className="text-white font-semibold">一般用戶</div>
              </div>
              <div className="bg-yellow-600/20 border border-yellow-500 rounded-lg p-3">
                <div className="text-yellow-300 text-sm">最後更新</div>
                <div className="text-white font-semibold">
                  {new Date().toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}

export default Users;
